import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import { getActiveCaisseYear, buildCaisseYearOptions } from '../utils/caisseActiveYear';
import { authJsonHeaders } from '../utils/authHeaders';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import './NHPage.css';
import './EtatInventory.css';

export default function AllDepensesPage() {
  const { id: clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [showTable, setShowTable] = useState(false);

  const [tab, setTab] = useState('mois');
  const [jourMode, setJourMode] = useState('date');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => getActiveCaisseYear());
  const years = buildCaisseYearOptions();

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      let url = '';
      const params = new URLSearchParams();

      if (clientId) {
        params.append('client_id', clientId);
      }

      if (tab === 'jour') {
        if (jourMode === 'date') {
          params.append('date', selectedDate);
          url = '/api/depenses/par-client';
        } else {
          params.append('date_debut', dateDebut);
          params.append('date_fin', dateFin);
          url = '/api/depenses/par-periode';
        }
      } else if (tab === 'mois') {
        params.append('mois', String(selectedMonth).padStart(2, '0'));
        params.append('annee', String(selectedYear));
        url = '/api/depenses/par-client';
      } else if (tab === 'annee') {
        params.append('annee', String(selectedYear));
        url = '/api/depenses/par-client';
      }

      const r = await fetch(`${url}?${params.toString()}`, { headers: authJsonHeaders() });
      const d = await r.json();
      if (d && d.success) {
        const list = d.depenses || [];
        setResults(list);
        setTotal(list.reduce((s, dep) => s + parseFloat(dep.montant || 0), 0));
        setShowTable(true);
      } else {
        setResults([]);
        setTotal(0);
        setShowTable(true);
        setError(d?.error || 'Erreur lors du chargement');
      }
    } catch (_) {
      setResults([]);
      setTotal(0);
      setShowTable(true);
      setError('Erreur réseau lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatMontant = (montant) => {
    const value = parseFloat(montant || 0);
    if (isNaN(value)) return '0,000 TND';
    return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;
  };

  return (
    <CgmModulePageLayout
      crumb="Comptabilité clients · Dépenses"
      title="Dépenses client"
      backTo={clientId ? `/client/${clientId}/charges` : '/'}
      wide
    >
      <div className="cgm-list-toolbar">
        <div className="cgm-filter-panel cgm-period-panel">
          <div className="cgm-filter-panel__title">Filtrer les dépenses client</div>
          <div className="cgm-period-body">
            <div className="cgm-period-tabs">
              <button type="button" className={`cgm-period-tab ${tab === 'jour' ? 'active' : ''}`} onClick={() => setTab('jour')}>Jour</button>
              <button type="button" className={`cgm-period-tab ${tab === 'mois' ? 'active' : ''}`} onClick={() => setTab('mois')}>Mois</button>
              <button type="button" className={`cgm-period-tab ${tab === 'annee' ? 'active' : ''}`} onClick={() => setTab('annee')}>Année</button>
            </div>

            {tab === 'jour' && (
              <div className="cgm-period-jour-panel">
                <div className="cgm-period-mode-group" role="group" aria-label="Mode jour">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="cliDepJourDate" name="cliDepJourMode" checked={jourMode === 'date'} onChange={() => setJourMode('date')} />
                    <label className="form-check-label" htmlFor="cliDepJourDate">Par date</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="cliDepJourPeriode" name="cliDepJourMode" checked={jourMode === 'periode'} onChange={() => setJourMode('periode')} />
                    <label className="form-check-label" htmlFor="cliDepJourPeriode">Par période</label>
                  </div>
                </div>
                <div className="cgm-period-filter-row">
                  {jourMode === 'date' ? (
                    <>
                      <label className="mb-0">Date</label>
                      <input type="date" className="hono-input-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                    </>
                  ) : (
                    <>
                      <label className="mb-0">De</label>
                      <input type="date" className="hono-input-date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                      <label className="mb-0">à</label>
                      <input type="date" className="hono-input-date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
                    </>
                  )}
                </div>
              </div>
            )}

            {tab === 'mois' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Mois</label>
                <select className="hono-select-month" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {tab === 'annee' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="cgm-period-actions">
              <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary" onClick={handleSearch} disabled={loading}>
                {loading ? 'Recherche…' : 'Rechercher'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border" style={{ color: '#0f766e' }} role="status">
            <span className="visually-hidden">Chargement…</span>
          </div>
        </div>
      )}

      {!loading && showTable && results.length > 0 && (
        <div className="nh-modern-table-card nh-modern-table-card--depenses-cgm">
          <div className="nh-modern-table-toolbar">
            <div className="nh-modern-table-info">
              {results.length} dépense{results.length > 1 ? 's' : ''} · Total{' '}
              <strong style={{ color: '#dc2626' }}>{formatMontant(total)}</strong>
            </div>
          </div>
          <div className="overview-table-container nh-modern-table-scroll">
            <table className="table overview-table nh-overview-data-table nh-modern-data-table">
              <thead>
                <tr>
                  <th><span className="nh-modern-th-inner">Date</span></th>
                  <th><span className="nh-modern-th-inner">Client</span></th>
                  <th><span className="nh-modern-th-inner">Libellé</span></th>
                  <th><span className="nh-modern-th-inner">Montant</span></th>
                </tr>
              </thead>
              <tbody>
                {results.map((dep, idx) => {
                  const clientName = dep.client || dep.beneficiaire || '';
                  let libelleText = dep.libelle || dep.description || '—';
                  libelleText = libelleText
                    .replace(/^\[CGM PAYÉ\]\s*/i, '[PAYÉ PAR CGM] ')
                    .replace(/^\[CGM\]\s*/i, '[PAYÉ PAR CGM] ');
                  const rawText = (dep.libelle || dep.description || '').toUpperCase();
                  const isHonoraire =
                    rawText.includes('HONORAIRES REÇU') ||
                    rawText.includes('HONORAIRES RECU') ||
                    rawText.includes('AVANCE');
                  return (
                    <tr key={dep.id || idx}>
                      <td>{formatDate(dep.date)}</td>
                      <td>{(clientName || '—').toString().toUpperCase()}</td>
                      <td>{String(libelleText).toUpperCase()}</td>
                      <td className="text-end">
                        <span
                          className="nh-badge-amount"
                          style={{ color: isHonoraire ? '#059669' : '#dc2626' }}
                        >
                          {formatMontant(dep.montant)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>TOTAL</strong></td>
                  <td className="text-end"><strong style={{ color: '#dc2626' }}>{formatMontant(total)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!loading && showTable && results.length === 0 && !error && (
        <div className="text-center text-muted py-4">Aucune dépense pour ces critères.</div>
      )}

      {!loading && !showTable && (
        <div className="text-center text-muted py-4">
          Choisissez une période puis cliquez sur Rechercher.
        </div>
      )}
    </CgmModulePageLayout>
  );
}
